export type ExploreViewport = [number, number, number, number];

export const LOCAL_MAP_RADIUS_M = 1_000;
export const LOCAL_MAP_MIN_ZOOM = 13;

type Coordinates = readonly [number, number];
type MapPoint = { lng: number; lat: number };
export interface LocalCamera {
  getCanvas(): { clientWidth: number; clientHeight: number };
  getCenter(): MapPoint;
  getZoom(): number;
  getMaxZoom(): number;
  unproject(point: [number, number]): MapPoint;
  jumpTo(camera: { center: [number, number]; zoom: number }): unknown;
}

export function localDistanceM(a: Coordinates, b: Coordinates): number {
  const radians = Math.PI / 180;
  const h =
    Math.sin(((b[1] - a[1]) * radians) / 2) ** 2 +
    Math.cos(a[1] * radians) *
      Math.cos(b[1] * radians) *
      Math.sin(((b[0] - a[0]) * radians) / 2) ** 2;
  return 12_742_000 * Math.asin(Math.sqrt(Math.min(1, h)));
}

/** Ground corners, not a zoom heuristic: pitch, bearing and CSS aspect ratio all matter. */
export function localCameraCorners(map: LocalCamera): Coordinates[] {
  const { clientWidth: width, clientHeight: height } = map.getCanvas();
  if (width <= 0 || height <= 0) return [];
  return (
    [
      [0, 0],
      [width, 0],
      [width, height],
      [0, height],
    ] as [number, number][]
  ).map((point) => {
    const location = map.unproject(point);
    return [location.lng, location.lat] as const;
  });
}

/** Synchronous, bounded correction before the next render. Caller guards reentrant move events.
 * The camera-centre footprint plus GPS-to-centre distance conservatively bounds every corner.
 * Flat Mercator ground at the supported <=36 degree pitch has no horizon in this viewport.
 * Keep 10m inside the product limit for projection/rounding; verify actual corners after changes.
 */
export function constrainLocalCamera(map: LocalCamera, origin: Coordinates): boolean {
  for (let attempt = 0; attempt < 8; attempt += 1) {
    const corners = localCameraCorners(map);
    if (corners.length !== 4) return false;
    const { lng, lat } = map.getCenter();
    const center: Coordinates = [lng, lat];
    const footprint = Math.max(...corners.map((point) => localDistanceM(center, point)));
    const offset = localDistanceM(origin, center);
    if (!Number.isFinite(footprint + offset)) return false;
    if (corners.every((point) => localDistanceM(origin, point) <= LOCAL_MAP_RADIUS_M - 10)) {
      return true;
    }
    if (footprint > 800) {
      map.jumpTo({
        center: [origin[0], origin[1]],
        zoom: Math.min(
          map.getMaxZoom(),
          map.getZoom() + Math.max(0.25, Math.log2(footprint / 750)),
        ),
      });
    } else {
      const fraction = Math.min(1, Math.max(0, 980 - footprint) / Math.max(1, offset));
      const longitudeDelta = ((((lng - origin[0] + 180) % 360) + 360) % 360) - 180;
      map.jumpTo({
        center: [origin[0] + longitudeDelta * fraction, origin[1] + (lat - origin[1]) * fraction],
        zoom: map.getZoom(),
      });
    }
  }
  return (
    localCameraCorners(map).length === 4 &&
    localCameraCorners(map).every(
      (point) => localDistanceM(origin, point) <= LOCAL_MAP_RADIUS_M - 10,
    )
  );
}

/** Unwrapped longitude lets MapLibre constrain a continuous view across the dateline. */
export function localMapBounds(
  center: readonly [number, number],
): [[number, number], [number, number]] {
  const latitude = Math.max(-85, Math.min(85, center[1]));
  const latitudeDelta = LOCAL_MAP_RADIUS_M / 111_320;
  const longitudeDelta = latitudeDelta / Math.max(0.087, Math.cos((latitude * Math.PI) / 180));
  return [
    [center[0] - longitudeDelta, Math.max(-85, latitude - latitudeDelta)],
    [center[0] + longitudeDelta, Math.min(85, latitude + latitudeDelta)],
  ];
}

export function localViewport(bbox: ExploreViewport): ExploreViewport {
  const wrap = (value: number) => ((((value + 180) % 360) + 360) % 360) - 180;
  return [wrap(bbox[0]), Math.max(-85, bbox[1]), wrap(bbox[2]), Math.min(85, bbox[3])].map(
    (value) => Number(value.toFixed(5)),
  ) as ExploreViewport;
}
